import { datadogRum } from '@datadog/browser-rum';
import React from 'react';
import { toast } from 'react-toastify';

import { Alert } from '../components/atoms/alert';
import { HttpCodes } from '../enums/httpCodes';
import { EventsService } from '../services/events.service';
import { useDownload } from './useDownload';

const handleEvidenceError = async (err: any) => {
  if (err?.status === HttpCodes.CONFLICT) {
    let body: any;
    try {
      body = await err.json();
    } catch {
      toast.error('Reissuance violation: evidence already exists for this plate.');

      return;
    }

    await new Alert(
      <span>
        An evidence PDF was already generated for this license plate at this location.{' '}
        <a href={`/portalList?previewEventID=${body.conflicting_event_id}`} target="_blank" rel="noreferrer">
          View conflicting event #{body.conflicting_event_id} ↗
        </a>
      </span>,
      'OK',
    ).promise;
  } else {
    let message = 'Failed to generate evidence. Please try again.';
    try {
      const body = typeof err?.json === 'function' ? await err.json() : err;
      if (body?.message) {
        message = `Failed to generate evidence: ${body.message}`;
      }
    } catch {
      // Use default message
    }
    toast.error(message);
  }
};

/**
 * Supports multiple concurrent evidence generation tasks tracked by event ID.
 * Each task runs independently — generating evidence for one event does not
 * block interactions with another event in the review queue.
 */
export const usePrintableEvidence = () => {
  const [activeEvents, setActiveEvents] = React.useState<Map<number, number>>(new Map());
  const { checkIfFileExistsAndDownload, checkIfFileExistsAndPrint } = useDownload();

  const isDownloadingForEvent = React.useCallback(
    (eventId: number | undefined) => eventId != null && (activeEvents.get(eventId) ?? 0) > 0,
    [activeEvents],
  );

  const isDownloading = activeEvents.size > 0;

  const generateEvidence = React.useCallback(async (eventId: number, mode: 'print' | 'download') => {
    setActiveEvents((prev) => {
      const next = new Map(prev);
      next.set(eventId, (next.get(eventId) ?? 0) + 1);

      return next;
    });

    const start = performance.now();

    try {
      let evidence;
      try {
        evidence = await EventsService.generatePrintableEvidence(eventId);
      } catch (err: any) {
        await handleEvidenceError(err);

        return;
      }

      if (!evidence || typeof evidence !== 'object' || typeof evidence.url !== 'string' || !evidence.url) {
        toast.error('Failed to generate evidence: unexpected response from server.');

        return;
      }

      const getUrl = evidence.url;

      const succeeded = mode === 'print'
        ? await checkIfFileExistsAndPrint(getUrl, `${eventId}.pdf`, false, 3000)
        : await checkIfFileExistsAndDownload(getUrl, `${eventId}.pdf`, false, 3000);

      if (succeeded) {
        datadogRum.addTiming('evidenceGeneration', performance.now() - start);
      }
    } finally {
      setActiveEvents((prev) => {
        const next = new Map(prev);
        const count = (next.get(eventId) ?? 1) - 1;
        if (count <= 0) {
          next.delete(eventId);
        } else {
          next.set(eventId, count);
        }

        return next;
      });
    }
  }, [checkIfFileExistsAndDownload, checkIfFileExistsAndPrint]);

  const printEvidence = React.useCallback(
    (eventId: number) => generateEvidence(eventId, 'print'),
    [generateEvidence],
  );

  const downloadEvidence = React.useCallback(
    (eventId: number) => generateEvidence(eventId, 'download'),
    [generateEvidence],
  );

  return { isDownloading, isDownloadingForEvent, generateEvidence, printEvidence, downloadEvidence };
};
